"use client";

import React from "react";
import Hero from "@/components/tools/Hero";
import RelatedTopics from "@/components/tools/RelatedTopics";
import AffordableExtractionsIntro from "@/components/services/extractions/AffordableExtractionsIntro";
import ReliableToothExtractions from "@/components/services/extractions/CommonReasonsForExtractions";
import WhatIsToothExtraction from "@/components/services/extractions/WisdomTeethRemovalSigns";
import TypesOfExtractions from "@/components/services/extractions/SimpleVsSurgicalExtractions";
import SameDayEmergencyExtractions from "@/components/services/extractions/EmergencyToothExtractions";
import AfterToothExtraction from "@/components/services/extractions/PostExtractionReplacement";
import AffordableFinancing from "@/components/services/extractions/ToothExtractionCosts";
import WhyChooseSmile4Me from "@/components/services/extractions/WhyChooseSmile4Me";
import ExtractionsLocation from "@/components/services/extractions/AffordableExtractionsLocation";
import ScheduleExtractionCta from "@/components/services/extractions/GetAffordableExtractions";
import FAQ from "@/components/tools/FAQ";

const extractionsFAQs = [
  {
    question: "Does a tooth extraction hurt?",
    answer:
      "Dr. Khaimov administers local anesthesia before every extraction, so you should feel pressure and movement during the procedure but not pain. If you feel discomfort at any point, let us know immediately, and we'll adjust. Most patients are surprised by how manageable the experience is.",
  },
  {
    question: "How long does a tooth extraction take?",
    answer:
      "Simple extractions typically take 20–40 minutes from the time you're seated. Surgical extractions may take longer depending on the tooth's position and complexity. Dr. Khaimov will give you a realistic time estimate after reviewing your X-rays.",
  },
  {
    question: "Can I drive myself home after an extraction?",
    answer:
      "For simple extractions under local anesthesia, most patients can drive themselves home. If you opt for sedation, you'll need someone to drive you. Our team will confirm this with you ahead of time.",
  },
  {
    question: "When should I call the dentist after an extraction?",
    answer:
      "Contact us right away if you experience heavy bleeding that won't stop after an hour, severe or worsening pain after the first few days, swelling that gets worse instead of better, fever, or any signs of infection. As a dental emergency in Astoria, these symptoms warrant prompt attention.",
    links: [
      {
        text: "dental emergency",
        url: "/blog/what-qualifies-as-a-dental-emergency/",
      },
    ],
  },
  {
    question: "What's the difference between a simple and surgical extraction?",
    answer:
      "A simple extraction removes a tooth that's visible and accessible in the mouth. A surgical extraction is needed when the tooth is impacted, broken below the gum, or hasn't fully erupted. Both are performed right here at our Astoria office.",
  },
];

const Extractions: React.FC = () => {
  return (
    <>
      <Hero
        imageSrc="/images/photo-tooth-extractions-in-astoria-queens-extracted-tooth-with-instruments.webp"
        imageAlt="photo-tooth-extractions-in-astoria-queens-extracted-tooth-with-instruments"
        title="Tooth Extractions in Astoria Queens, NY"
        ctaText="Book Now"
        ctaLink="/contact"
      />
      <main className="flex-1 py-8">
        <div className="flex flex-col lg:flex-row mx-auto px-4 sm:px-8 md:px-12">
          <article className="flex-1 space-y-8 pr-0 lg:pr-8">
            <AffordableExtractionsIntro />
            <ReliableToothExtractions />
            <WhatIsToothExtraction />
            <TypesOfExtractions />
            <SameDayEmergencyExtractions />
            <AfterToothExtraction />
            <AffordableFinancing />
            <WhyChooseSmile4Me />
            <ExtractionsLocation />
            <FAQ title="Frequently Asked Questions" faqs={extractionsFAQs} />
            <ScheduleExtractionCta />
          </article>
          <RelatedTopics
            topics={[
              { label: "Emergency Treatment", url: "/services/emergency-treatment/" },
              { label: "Root Canals", url: "/services/root-canals/" },
              { label: "Implant Dentistry", url: "/services/implant-dentistry/" },
              { label: "Dental Bridges", url: "/services/dental-bridges/" },
              { label: "Dentures", url: "/services/dentures/" },
              { label: "Sedation Dentistry", url: "/services/sedation-dentistry/" },
            ]}
          />
        </div>
      </main>
    </>
  );
};

export default Extractions;
